/**
 * paletteTransforms.ts — HSL transform deltas for runtime palette-swap shader.
 *
 * # What this file owns (Layer 4b, Option B)
 *
 * The 4 HSL transformation parameters per `PaletteId` that the shader
 * applies to every non-transparent pixel of Prime's sprite. Together,
 * these define how each cosmetic palette variant looks at runtime.
 *
 *   - `hueShift`   — degrees to rotate the H component on the color wheel.
 *                    Range: -180 to +180. Positive shifts toward red→orange→yellow,
 *                    negative shifts toward red→magenta→blue.
 *
 *   - `satScale`   — multiplier applied to the S component.
 *                    Range: 0.0 (full grayscale) to 2.0 (super-saturated).
 *                    1.0 = no change.
 *
 *   - `lightShift` — additive offset applied to the L component (after deltas).
 *                    Range: -0.5 (much darker) to +0.5 (much brighter).
 *                    0.0 = no change.
 *
 *   - `contrast`   — multiplier applied around L=0.5 to expand or compress
 *                    the lightness range. Range: 0.5 (low contrast / flat)
 *                    to 1.5 (high contrast / punchy). 1.0 = no change.
 *                    Applied as: L_new = (L - 0.5) * contrast + 0.5
 *
 * # Why HSL hue-rotation, not RGB palette swap (LOCKED Layer 4b, Option B)
 *
 * The AUTeddy "Cyber Marksman" sprite is an indexed PNG with 116 actively-
 * used palette entries clustered into ~5 logical color groups (cream shell,
 * mustard accent, slate mid, charcoal shadow, bright-yellow visor). Each
 * group contains ~15-20 anti-aliased gradient steps baked directly into
 * the indexed palette.
 *
 * An RGB-replacement shader (replace 5 base colors with 5 replacement
 * colors, ε-tolerance) would only match ~1/15 of the actually-rendered
 * pixels — recoloring the saturated peaks of each gradient and leaving
 * AA shoulders original-colored. The result is a patchy, broken sprite.
 *
 * HSL hue-rotation works correctly on indexed-PNG pixel art because:
 *
 *   1. It treats the sprite as a continuous color field, not a discrete
 *      palette. Every pixel gets transformed regardless of which palette
 *      index it came from.
 *
 *   2. Gradient structure is preserved automatically. If the artist drew
 *      cream→tan→darker-tan as a gradient, hue-rotation produces
 *      red→darker-red→darkest-red — same gradient, different hue. AA
 *      stays coherent without per-pixel lookup tables.
 *
 *   3. It scales to ANY sprite asset. New character sourced from a
 *      different artist with a different palette structure works with
 *      the same shader, no per-asset color extraction.
 *
 *   4. Solo-dev precedent: this is the pattern Vampire Survivors, Brotato,
 *      Halls of Torment, and Hades' cosmetic tints all use. Industry-
 *      standard for pixel-art cosmetic variants.
 *
 * # Design intent per palette (LOCKED v1, TUNE-IN-DESIGN-PASS)
 *
 * Each palette ties to a faction or aesthetic from `AevonsWake_04_LORE.md`:
 *
 *   - `default`         — Prime's base appearance. No transform applied at
 *                         runtime (the SpriteRenderer skips filter assignment
 *                         entirely when paletteId === 'default'). The entry
 *                         exists here for API symmetry and to make adding
 *                         new palettes a single-file change.
 *
 *   - `crimson`         — Blood-marked Compact veteran aesthetic. Mild hue
 *                         shift toward red, slight saturation boost. Reads
 *                         as "same shell, painted red" rather than "different
 *                         character". Premium cosmetic.
 *
 *   - `cyan_glitch`     — Severed-faction glitch-corruption aesthetic.
 *                         180° hue rotation puts cream→teal and
 *                         mustard→cyan. Slight saturation boost, mild
 *                         contrast bump. Reads as "Severed-corrupted Prime."
 *                         Premium cosmetic.
 *
 *   - `compact_loyal`   — Compact-faction regular military livery (vs the
 *                         crimson "veteran" variant). Cool blue/silver hue
 *                         shift, desaturated for formal/regulation tone.
 *                         Slightly darker. Reads as "Compact-regulars Prime."
 *
 *   - `aevon_corrupted` — Aevon-tech overgrowth aesthetic. Sickly green/
 *                         violet hue shift, reduced saturation, darker
 *                         lightness, increased contrast for "diseased"
 *                         look. Reads as Aevon-Risen affected.
 *
 *   - `pale_decay`      — Pale-faction decay aesthetic. No hue shift, but
 *                         heavy desaturation and reduced lightness, mild
 *                         contrast reduction for washed-out / decaying
 *                         look. Reads as Pale-faction infiltration.
 *
 * # First-pass values are designer-tunable
 *
 * The values below are first-pass — designed against lore intent, but
 * exact numbers will iterate during the cosmetic-polish phase once the
 * shader is rendering against actual sprites in-browser. The shader
 * algorithm IS correct; only the per-palette parameter values are tunable.
 *
 * Marked `TUNE-IN-DESIGN-PASS` — they will adjust, but they are NOT
 * placeholder garbage. First-load will show distinct, coherent, lore-
 * appropriate palette variants, not broken-looking sprites.
 *
 * # Adding new palettes later
 *
 * Adding a new PaletteId is structurally cheap (~5 min per palette):
 *
 *   1. Add new literal to `PaletteId` union in
 *      `packages/core/src/state/types.ts`
 *   2. Add new entry to `PALETTE_TRANSFORMS` map below with the four HSL
 *      deltas. Defaults of `{ hueShift: 0, satScale: 1, lightShift: 0,
 *      contrast: 1 }` produce no visible change — start there and iterate.
 *   3. Existing tests will continue passing — they're keyed off
 *      `PALETTE_TRANSFORMS` map keys, not hardcoded enums
 *
 * No SAVE_VERSION bump required. `cosmeticPaletteId: PaletteId` in
 * Loadout already accepts any union member; new members are forward-
 * compatible.
 *
 * # Shader uniform packing (informational, used by paletteFilter.ts)
 *
 * The shader receives a single vec4 uniform per active filter:
 *
 *     uPaletteTransform = (hueShift_radians, satScale, lightShift, contrast)
 *
 * `hueShift` is converted from degrees to radians at filter-construction
 * time so the shader doesn't pay the conversion cost per-pixel.
 *
 * @see paletteShader.glsl.ts — GLSL fragment shader that consumes these
 * @see paletteShader.wgsl.ts — WGSL fragment shader (WebGPU companion)
 * @see paletteFilter.ts — PixiJS v8 Filter subclass that wraps the shaders
 */

import type { PaletteId } from '@aevons-wake/core';

/**
 * Four HSL transformation deltas applied per non-transparent pixel by
 * the palette-swap fragment shader.
 *
 * Application order in shader (after RGB→HSL conversion):
 *
 *   1. H = (H + hueShift_radians) mod 2π
 *   2. S = clamp(S * satScale, 0, 1)
 *   3. L = clamp((L - 0.5) * contrast + 0.5 + lightShift, 0, 1)
 *
 * Then HSL→RGB conversion and re-premultiplication of alpha.
 */
export interface PaletteTransform {
  /**
   * Hue rotation in degrees. -180 to +180.
   *
   * Negative rotates toward magenta/red/orange (warm shift).
   * Positive rotates toward green/cyan/blue (cool shift).
   * 0 = no change.
   *
   * Converted to radians at filter-construction time.
   */
  readonly hueShift: number;

  /**
   * Saturation multiplier. 0.0 to 2.0.
   *
   * 0.0 = full grayscale.
   * 1.0 = no change.
   * 2.0 = super-saturated.
   *
   * Result is clamped to [0, 1] in shader to prevent out-of-gamut values.
   */
  readonly satScale: number;

  /**
   * Lightness additive offset. -0.5 to +0.5.
   *
   * Negative darkens, positive brightens.
   * 0.0 = no change.
   *
   * Applied AFTER contrast and BEFORE clamping.
   */
  readonly lightShift: number;

  /**
   * Lightness contrast multiplier around L=0.5. 0.5 to 1.5.
   *
   * 0.5 = compress lightness toward middle gray (low contrast).
   * 1.0 = no change.
   * 1.5 = expand lightness away from middle gray (high contrast).
   *
   * Applied as: L = (L - 0.5) * contrast + 0.5
   */
  readonly contrast: number;
}

/**
 * Per-palette HSL transforms.
 *
 * `default` is included for API symmetry but unused at runtime — the
 * SpriteRenderer skips filter assignment entirely when an entity's
 * `cosmeticPaletteId === 'default'` (per Decision 5). The identity
 * transform here documents what "no change" means and provides a
 * fallback if external code does query the map for `'default'`.
 *
 * All values are first-pass (TUNE-IN-DESIGN-PASS).
 */
export const PALETTE_TRANSFORMS: Readonly<Record<PaletteId, PaletteTransform>> = {
  // ─── No-op (filter skipped at sprite-level) ───────────────────────────────
  default: {
    hueShift: 0,
    satScale: 1.0,
    lightShift: 0.0,
    contrast: 1.0,
  },

  // ─── Crimson — blood-marked Compact veteran ───────────────────────────────
  // Cream → cream-with-red-cast, mustard → red-orange.
  // Mild shift, slight saturation bump for "painted veteran" look.
  crimson: {
    hueShift: -40,
    satScale: 1.15,
    lightShift: 0.0,
    contrast: 1.0,
  },

  // ─── Cyan-glitch — Severed-faction corruption ─────────────────────────────
  // 180° rotation: cream→teal, mustard→cyan. Saturation boost emphasizes
  // the "glitch" reading. Mild contrast bump and slight darken for
  // "corrupted" tone.
  cyan_glitch: {
    hueShift: 180,
    satScale: 1.30,
    lightShift: -0.05,
    contrast: 1.1,
  },

  // ─── Compact-loyal — regular Compact military livery ──────────────────────
  // Cool blue/silver shift with desaturation for formal regulation feel.
  // Slightly darker than default. Distinct from crimson "veteran" variant.
  compact_loyal: {
    hueShift: -120,
    satScale: 0.85,
    lightShift: -0.10,
    contrast: 1.0,
  },

  // ─── Aevon-corrupted — Aevon-Risen tech overgrowth ────────────────────────
  // Green/violet shift toward "diseased" reading. Reduced saturation,
  // darker, increased contrast for unsettling appearance.
  aevon_corrupted: {
    hueShift: 90,
    satScale: 0.7,
    lightShift: -0.15,
    contrast: 1.2,
  },

  // ─── Pale-decay — Pale-faction decay ──────────────────────────────────────
  // No hue shift — Pale aesthetic is about washed-out and dim, not
  // recolored. Heavy desaturation, darker lightness, mild contrast
  // reduction for "fading" tone.
  pale_decay: {
    hueShift: 0,
    satScale: 0.2,
    lightShift: -0.20,
    contrast: 0.85,
  },
} as const;

/**
 * Convert hueShift from degrees to radians for shader uniform.
 *
 * Called by `PaletteFilter` at construction time so the shader doesn't
 * pay this trivial conversion cost per-fragment. Exported for testability.
 *
 * @param degrees Hue shift in degrees, typically -180 to +180.
 * @returns Hue shift in radians, used as a uniform input to the shader.
 *
 * @example
 *   degreesToRadians(180)   // → 3.141592653589793
 *   degreesToRadians(-40)   // → -0.6981317007977318
 *   degreesToRadians(0)     // → 0
 */
export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
